import { useState } from "react";
import { useForm } from "react-hook-form";
import FormInput from "./FormInput";
import { minDelay, readFileAsArrayBuffer } from "../util";
import Actor from "./Actor";
import LoadingOverlay from './LoadingOverlay';
import { useConfig } from "../ConfigContext";

export default function ExtensionRequest({ caseData, onExtensionRequestComplete }) {
  const {config, error: configError} = useConfig();
  const [processingError, setProcessingError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    getValues,
    setError
  } = useForm({
    defaultValues: {
      "firstName": caseData.apt_Recipient.firstname,
      "lastName": caseData.apt_Recipient.lastname,
      "email": caseData.apt_Recipient.emailaddress1,
      "emailConfirm": caseData.apt_Recipient.emailaddress1,
      "phone": caseData.apt_Recipient.telephone2,
      "streetAddress": caseData.apt_Recipient.address1_line1,
      "municipality": caseData.apt_Recipient.address1_city,
      "province": caseData.apt_Recipient.address1_stateorprovince,
      "postalCode": caseData.apt_Recipient.address1_postalcode
    },
  });
  const actor = watch('actor');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    data.type = config.globalOptions.find(globalOption => globalOption.Name === 'apt_reviewtype')?.Options.find(option => option.Label === 'Extension')?.Value;
    data.channel = config.globalOptions.find(globalOption => globalOption.Name === 'apt_paymentchannel')?.Options.find(option => option.Label === 'Online')?.Value;
    data.requestMethod = config.globalOptions.find(globalOption => globalOption.Name === 'apt_screeningmethod')?.Options.find(option => option.Label === 'Written')?.Value;

    if(data.files.length > 0){
      setIsUploading(true);
      for (const file of data.files) {
        setIsUploading(file.name);
        try {
          const binaryData = await readFileAsArrayBuffer(file);
          let response = await minDelay(fetch(`${config.apiUrl}/cases/${caseData.apt_caseid}/file/${file.name}`,{
            method: 'PUT',
            headers: {
              'Api-Key': config.apiKey,
            },
            body: binaryData
          }), 1000);
          if(response.ok){
            let responseJSON = await response.json();
            console.log(responseJSON);
            file["apt_documentid"] = responseJSON.apt_documentid;
          }else{
            const responseText = await response.text();
            console.error(`Error uploading file ${file.name}: ${responseText}`);
            setError('files', { message: `Error uploading file ${file.name}: ${responseText}` });
            setIsUploading(false);
            setIsSubmitting(false);
            document.getElementById('files').scrollIntoView({ behavior: "smooth" });
            return;
          }
        } catch (error) {
          console.error(`Error uploading file ${file.name}: ${error}`);
          setError('files', { message: `Error uploading file ${file.name}` });
          setIsUploading(false);
          setIsSubmitting(false);
          return;
        }
      }
      setIsUploading(false);
    }

    minDelay(
      fetch(`${config.apiUrl}/cases/${caseData.apt_caseid}/request-review`, {
        method: "POST",
        headers: {
          "Api-Key": config.apiKey,
          "Content-Type": "application/json",
          // "Ocp-Apim-Trace": "true"
        },
        body: JSON.stringify(data),
      }),
      1000
    ).then((response) => {
      if (response.ok) {
        setIsSubmitting(false);
        onExtensionRequestComplete();
      }
      else {
        console.error(response.statusText);
        setProcessingError("There was an error submitting your request. Please contact us at 1-888-448-0000 or apt@york.ca.");
        setIsSubmitting(false);
      }
    }).catch((err) => {
      console.error(err);
      setProcessingError("There was an error submitting your request. Please contact us at 1-888-448-0000 or apt@york.ca.");
      setIsSubmitting(false);
    });
  };

  return (
    <div className="path-webform">
      <p>
      This extentsion request is to ask for more time to submit a screening review request. This is not a request for a payment extension. 
      </p>
      <p>
        Please review the plate owner information below and make corrections if
        necessary. An email address is required to submit a screening extension request.
      </p>
      <form
        className="webform-submission-form webform-submission-add-form webform-submission-test-form webform-submission-test-add-form js-webform-details-toggle webform-details-toggle antibot contextual-region"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper" style={{marginTop:'1rem'}}>
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <FormInput
                id="actor"
                label="I am"
                type="radio"
                register={register("actor", {required: "This is required"})}
                error={errors.actor}
                options={[{value:'owner',label:"The Vehicle Plate Owner identified on the penalty order"},{value:'authorized',label:"Authorized to act on behalf of the Vehicle Plate Owner"}]}
                required
              />
            </div>
          </div>
        </div>

        <Actor type="owner" readonly={true} register={register} control={control} errors={errors} setValue={setValue} />
        
        {actor === 'authorized' && (
          <Actor type="authorized" register={register} control={control} errors={errors} setValue={setValue} />
        )}
       
        <div className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper" style={{marginTop:'1rem'}}>
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <FormInput
                id="requestReason"
                label="Reason for Request"
                description="Why you require additional time to request a screening review"
                type="textarea"
                register={register("requestReason", {required: "A reason for the request is required"})}
                error={errors.requestReason}
                maxLength={2000}
                required
              />
            </div>
          </div>
        </div>
        <div
          className="webform-flexbox js-webform-flexbox js-form-wrapper form-wrapper"
          style={{ marginTop: "1rem" }}
        >
          <div className="webform-flex webform-flex--1">
            <div className="webform-flex--container">
              <FormInput
                id="files"
                type="file"
                label="Supporting Documents"
                description="Maximum of 3 documents (10MB maximum size)"
                control={control}
                rules={{
                  validate: (value, formValues) =>
                    (value.length === 0 || !caseData.apt_Document_apt_Case_apt_Case.find((existingDoc) => existingDoc.apt_name == value[0].name)) || "Filename already exists",
                }}
                error={errors.files}
              />
            </div>
          </div>
        </div>

        <FormInput
                id="termsOfService"
                label="I have read and agree to the Terms of Service"
                type="checkbox"
                register={register("termsOfService", {required: "You must agree to the terms of service"})}
                error={errors.termsOfService}
                required
              />
        <div className="webform-flex--1 js-form-item form-item js-form-type-textfield form-type-textfield">
          <button
            id="submitButton"
            className="webform-button--submit button button--secondary js-form-submit form-submit"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
        { processingError && <h3 className="request-processing-error">{processingError}</h3> }
      </form>
      {isSubmitting && <LoadingOverlay label={isUploading ? `Uploading ${isUploading}` : 'Submitting Request'} />}
    </div>
  );
}
